<template>
  <div
    v-if="provenance"
    class="cc"
    :class="{ 'cc-compact': compact, 'cc-plain': plain, 'cc-lime': tone === 'lime', 'cc-right': align === 'right' }"
    @mouseenter="hoverOpen"
    @mouseleave="hoverClose"
  >
    <!-- plain: non-interactive indicator, safe to nest inside links/cards -->
    <span v-if="plain" class="cc-badge cc-badge-plain" :title="badgeTitle">
      <svg class="cc-ic" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 3l7 2.6v5.1c0 4.3-3 8.2-7 9.3-4-1.1-7-5-7-9.3V5.6L12 3z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" />
        <path d="M8.8 11.7l2.2 2.2 4.2-4.6" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
      <span class="cc-lab">{{ compact ? 'Verified' : 'Content Credentials' }}</span>
    </span>

    <template v-else>
      <button
        class="cc-badge"
        type="button"
        :title="badgeTitle"
        :aria-expanded="open ? 'true' : 'false'"
        @click.stop="toggle"
      >
        <svg class="cc-ic" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 3l7 2.6v5.1c0 4.3-3 8.2-7 9.3-4-1.1-7-5-7-9.3V5.6L12 3z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" />
          <path d="M8.8 11.7l2.2 2.2 4.2-4.6" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <span class="cc-lab">{{ compact ? 'Verified' : 'Content Credentials' }}</span>
      </button>

      <div v-show="open" class="cc-panel" role="dialog" aria-label="Content Credentials">
        <div class="cc-head">
          <span class="cc-shield">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 3l7 2.6v5.1c0 4.3-3 8.2-7 9.3-4-1.1-7-5-7-9.3V5.6L12 3z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
              <path d="M8.6 11.7l2.3 2.3 4.5-4.9" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
          <span class="cc-htext">
            <b>Content Credentials</b>
            <span class="cc-sub">Cryptographically signed · tamper-evident</span>
          </span>
        </div>

        <dl class="cc-rows">
          <div class="cc-row"><dt>Issued by</dt><dd>{{ provenance.issuer }}</dd></div>
          <div v-if="signedAtLabel" class="cc-row"><dt>Signed</dt><dd>{{ signedAtLabel }}</dd></div>
          <div class="cc-row"><dt>Integrity</dt><dd class="cc-ok">Unaltered since capture</dd></div>
          <div class="cc-row"><dt>AI-edited</dt><dd>{{ provenance.aiDerived ? 'Yes, AI-optimized' : 'No, original capture' }}</dd></div>
          <div v-if="provenance.transcriptCoupled" class="cc-row"><dt>Sourcing</dt><dd>Insights trace to the transcript</dd></div>
          <div v-if="shortDid" class="cc-row"><dt>Producer</dt><dd>{{ shortDid }}</dd></div>
          <div v-if="algoLabel" class="cc-row"><dt>Signature</dt><dd>{{ algoLabel }}</dd></div>
        </dl>

        <div v-if="files && files.length" class="cc-files">
          <div class="cc-files-h">Verified files</div>
          <div class="cc-chips">
            <span v-for="(f, i) in files" :key="i" class="cc-chip">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7.5 12.2l2.8 2.8 6-6.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
              {{ f }}
            </span>
          </div>
        </div>

        <a
          v-if="provenance.verifyUrl"
          class="cc-verify"
          :href="provenance.verifyUrl"
          target="_blank"
          rel="noopener noreferrer"
          @click.stop
        >
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" stroke-width="1.8" /><path d="M21 21l-4.3-4.3" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" /></svg>
          Open C2PA inspector
        </a>
        <p v-if="provenance.fileUrl" class="cc-hint">
          It reads the file itself: <a :href="provenance.fileUrl" download @click.stop>download the file</a> and drop it in.
        </p>

        <p class="cc-std">
          Signed to the <b>C2PA</b> standard<template v-if="provenance.trustPending"> · public trust-listing pending</template>
        </p>
      </div>
    </template>
  </div>
</template>

<script>
import { shortenDid } from '@/utils/provenance';

export default {
  name: 'ContentCredentials',
  props: {
    // normalized view-model from utils/provenance.js normalizeProvenance(); falsy → renders nothing
    provenance: { type: Object, default: null },
    // compact variant for cards/rows (shorter label)
    compact: { type: Boolean, default: false },
    // non-interactive indicator (no popover) - use inside <router-link>/cards
    plain: { type: Boolean, default: false },
    // optional list of file labels this credential covers (shown in the inspector)
    files: { type: Array, default: () => [] },
    // 'lime' matches the event hub's accent; default is the app's green
    tone: { type: String, default: 'default' },
    // 'right' anchors the popover to the badge's right edge (for right-aligned hosts)
    align: { type: String, default: 'left' },
  },
  data() {
    return { open: false };
  },
  computed: {
    badgeTitle() {
      if (!this.provenance) return '';
      return `Content Credentials, signed by ${this.provenance.issuer}, tamper-evident`;
    },
    signedAtLabel() {
      const v = this.provenance && this.provenance.signedAt;
      if (!v) return '';
      const t = Date.parse(v);
      if (isNaN(t)) return String(v);
      try {
        return new Date(t).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
      } catch (e) {
        return String(v);
      }
    },
    shortDid() {
      return this.provenance ? shortenDid(this.provenance.producerDid) : '';
    },
    algoLabel() {
      const a = this.provenance && this.provenance.algorithm;
      return a ? String(a).toUpperCase() : '';
    },
  },
  methods: {
    toggle() {
      this.open = !this.open;
    },
    hoverOpen() {
      clearTimeout(this._ct);
      this.open = true;
    },
    hoverClose() {
      clearTimeout(this._ct);
      this._ct = setTimeout(() => {
        this.open = false;
      }, 140);
    },
    onDocClick(e) {
      if (this.open && this.$el && !this.$el.contains(e.target)) this.open = false;
    },
    onKey(e) {
      if (e.key === 'Escape') this.open = false;
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onKey);
  },
  beforeDestroy() {
    clearTimeout(this._ct);
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onKey);
  },
};
</script>

<style scoped>
/* Self-contained credential card.
   The hosts fight us on two fronts: App.vue forces a global monospace font with
   !important, and the event-hub applies a `* { margin:0; padding:0 }` reset whose
   specificity TIES with scoped styles (injection order decides who wins - flaky).
   So every layout-critical declaration here is !important: nothing outside this
   file can flatten the panel again. */
.cc {
  position: relative !important;
  display: inline-flex !important;
  line-height: 1.4 !important;
}
.cc-badge {
  display: inline-flex !important;
  align-items: center !important;
  gap: 7px !important;
  padding: 6px 14px 6px 11px !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  letter-spacing: 0.01em !important;
  color: inherit;
  background: rgba(127, 137, 122, 0.12) !important;
  border: 1px solid rgba(127, 137, 122, 0.34) !important;
  border-radius: 999px !important;
  cursor: pointer;
  white-space: nowrap !important;
  transition: background 0.18s ease, border-color 0.18s ease;
}
.cc-badge:hover {
  background: rgba(46, 158, 107, 0.16) !important;
  border-color: rgba(46, 158, 107, 0.5) !important;
}
.cc-ic {
  width: 14px !important;
  height: 14px !important;
  flex: 0 0 auto !important;
  color: #2ea36b !important;
}
.cc-compact .cc-badge {
  padding: 3px 8px 3px 6px !important;
  font-size: 10px !important;
}
.cc-compact .cc-ic {
  width: 12px !important;
  height: 12px !important;
}
.cc-badge-plain {
  cursor: default !important;
}
.cc-badge-plain:hover {
  background: rgba(127, 137, 122, 0.12) !important;
  border-color: rgba(127, 137, 122, 0.34) !important;
}

/* ---- popover inspector ---- */
.cc-panel {
  position: absolute !important;
  top: calc(100% + 10px) !important;
  left: 0 !important;
  z-index: 60 !important;
  width: 316px !important;
  padding: 20px !important;
  text-align: left !important;
  background: #15161d !important;
  border: 1px solid rgba(255, 255, 255, 0.11) !important;
  border-radius: 14px !important;
  box-shadow: 0 24px 56px -22px rgba(0, 0, 0, 0.78) !important;
  cursor: default;
}
.cc-panel * {
  box-sizing: border-box !important;
}
.cc-panel,
.cc-panel b,
.cc-panel span,
.cc-panel dt,
.cc-panel dd,
.cc-panel p,
.cc-panel a,
.cc-panel div {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
}

.cc-head {
  display: flex !important;
  align-items: center !important;
  gap: 12px !important;
  margin: 0 0 16px !important;
  padding: 0 0 16px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
}
.cc-shield {
  flex: 0 0 auto !important;
  width: 36px !important;
  height: 36px !important;
  border-radius: 11px !important;
  display: grid !important;
  place-items: center !important;
  color: #57d19c !important;
  background: rgba(87, 209, 156, 0.11) !important;
  border: 1px solid rgba(87, 209, 156, 0.28) !important;
}
.cc-shield svg {
  width: 20px !important;
  height: 20px !important;
}
.cc-htext {
  display: flex !important;
  flex-direction: column !important;
  gap: 3px !important;
  min-width: 0 !important;
}
.cc-htext b {
  font-size: 13.5px !important;
  font-weight: 700 !important;
  color: #ffffff !important;
  line-height: 1.2 !important;
}
.cc-sub {
  font-size: 10.5px !important;
  color: rgba(255, 255, 255, 0.48) !important;
  letter-spacing: 0.01em !important;
}

.cc-rows {
  display: block !important;
  margin: 0 !important;
  padding: 0 !important;
}
.cc-row {
  display: grid !important;
  grid-template-columns: 84px minmax(0, 1fr) !important;
  gap: 0 14px !important;
  align-items: baseline !important;
  padding: 5px 0 !important;
}
.cc-row dt {
  font-size: 11px !important;
  font-weight: 500 !important;
  color: rgba(255, 255, 255, 0.44) !important;
  margin: 0 !important;
  padding: 0 !important;
}
.cc-row dd {
  margin: 0 !important;
  padding: 0 !important;
  font-size: 12.5px !important;
  font-weight: 500 !important;
  line-height: 1.5 !important;
  color: #edeff5 !important;
}
.cc-ok {
  color: #6fdfac !important;
}

.cc-files {
  margin: 15px 0 0 !important;
  padding: 15px 0 0 !important;
  border-top: 1px solid rgba(255, 255, 255, 0.08) !important;
}
.cc-files-h {
  font-size: 9.5px !important;
  font-weight: 600 !important;
  letter-spacing: 0.1em !important;
  text-transform: uppercase !important;
  color: rgba(255, 255, 255, 0.4) !important;
  margin: 0 0 10px !important;
}
.cc-chips {
  display: flex !important;
  flex-wrap: wrap !important;
  gap: 7px !important;
}
.cc-chip {
  display: inline-flex !important;
  align-items: center !important;
  gap: 6px !important;
  padding: 5px 11px 5px 9px !important;
  font-size: 11.5px !important;
  font-weight: 500 !important;
  color: #e6e9f0 !important;
  background: rgba(255, 255, 255, 0.05) !important;
  border: 1px solid rgba(255, 255, 255, 0.09) !important;
  border-radius: 999px !important;
  white-space: nowrap !important;
}
.cc-chip svg {
  width: 12px !important;
  height: 12px !important;
  flex: 0 0 auto !important;
  color: #57d19c !important;
}

.cc-verify {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px !important;
  margin: 16px 0 0 !important;
  padding: 0 14px !important;
  height: 38px !important;
  font-size: 12.5px !important;
  font-weight: 600 !important;
  color: #0e1013 !important;
  background: #57d19c !important;
  border-radius: 10px !important;
  cursor: pointer;
  transition: background 0.16s ease;
}
.cc-verify svg {
  width: 13px !important;
  height: 13px !important;
}
.cc-verify:hover {
  background: #6fe0ac !important;
}
.cc-hint {
  margin: 10px 0 0 !important;
  padding: 0 !important;
  font-size: 10.5px !important;
  line-height: 1.5 !important;
  color: rgba(255, 255, 255, 0.44) !important;
}
.cc-hint a {
  color: #6fdfac !important;
  font-weight: 600 !important;
  text-decoration: none !important;
}
.cc-hint a:hover {
  text-decoration: underline !important;
}

.cc-std {
  margin: 14px 0 0 !important;
  padding: 0 !important;
  font-size: 10px !important;
  line-height: 1.5 !important;
  letter-spacing: 0.02em !important;
  color: rgba(255, 255, 255, 0.38) !important;
}
.cc-std b {
  color: rgba(255, 255, 255, 0.6) !important;
  font-weight: 700 !important;
}

/* ---- lime tone (event hub) ---- */
.cc-lime .cc-badge {
  color: #d7df23 !important;
  background: transparent !important;
  border-color: #d7df2340 !important;
}
.cc-lime .cc-badge:hover {
  background: #d7df231f !important;
  border-color: #d7df2380 !important;
}
.cc-lime .cc-ic,
.cc-lime .cc-chip svg {
  color: #d7df23 !important;
}
.cc-lime .cc-panel {
  background: #0b0c14 !important;
  border-color: #ffffff1f !important;
}
.cc-lime .cc-shield {
  color: #d7df23 !important;
  background: #d7df231f !important;
  border-color: #d7df2340 !important;
}
.cc-lime .cc-ok,
.cc-lime .cc-hint a {
  color: #d7df23 !important;
}
.cc-lime .cc-verify {
  background: #d7df23 !important;
}
.cc-lime .cc-verify:hover {
  background: #e3ea4a !important;
}
.cc-right .cc-panel {
  left: auto !important;
  right: 0 !important;
}

/* never let the popover exceed the viewport on small screens */
@media (max-width: 640px) {
  .cc-panel {
    width: auto !important;
    min-width: 250px !important;
    max-width: calc(100vw - 24px) !important;
  }
}
</style>
